import type { LinkSummary, ProcessingStatus, ScreenshotStatus } from '@/types/api';

/**
 * Whether a link still has background work queued or running. Enrichment and the screenshot run in
 * workers a few seconds to a minute after the request that queued them, so without this the page
 * would show whatever it loaded and never move on.
 */

const ENRICHMENT_IN_FLIGHT: readonly ProcessingStatus[] = ['RESERVED', 'ENRICHING', 'ENRICHED'];
const SCREENSHOT_IN_FLIGHT: readonly ScreenshotStatus[] = ['PENDING', 'PROCESSING'];

type Progress = Pick<LinkSummary, 'processingStatus' | 'screenshotStatus'>;

export function isEnrichmentInFlight(link: Progress): boolean {
  return ENRICHMENT_IN_FLIGHT.includes(link.processingStatus);
}

/** True while the screenshot is queued or capturing — including after enrichment queued it. */
export function isScreenshotInFlight(link: Progress): boolean {
  return (
    link.processingStatus === 'SCREENSHOT_PENDING' ||
    SCREENSHOT_IN_FLIGHT.includes(link.screenshotStatus)
  );
}

export function isLinkInFlight(link: Progress): boolean {
  return isEnrichmentInFlight(link) || isScreenshotInFlight(link);
}

/** How often an open detail page re-reads a link that has work in flight. */
export const IN_FLIGHT_POLL_MS = 3_000;

/**
 * How long one page keeps polling. A worker gives up well within this (enrichment and capture each
 * time out in seconds, SQS retries included), so a link still "in flight" past it is stuck — a
 * record whose job was lost — and polling it forever would only cost requests.
 */
export const MAX_IN_FLIGHT_POLL_MS = 5 * 60_000;
